import { FocusMonitor } from "@angular/cdk/a11y";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Component, forwardRef, Input, ViewChild, OnDestroy, Inject, ElementRef, Optional, Self } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { Subject } from "rxjs";
import { CurrencyPipe } from "@angular/common";


@Component({
  selector: "app-rcc-currency-input",
  templateUrl: "./rcc-currency.component.html",
  styleUrls: ["./rcc-currency.component.scss"],
  providers: [
    /*{
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RccCurrencyInput),
      multi: true
    },*/
    { provide: MatFormFieldControl, useExisting: RccCurrencyInput }
  ]
})
// tslint:disable-next-line: component-class-suffix
export class RccCurrencyInput implements ControlValueAccessor, MatFormFieldControl<number>, OnDestroy
{
  static nextId = 0;
  /* tslint:disable: variable-name */
  static ngAcceptInputType_disabled: boolean | string | null | undefined;
  static ngAcceptInputType_required: boolean | string | null | undefined;
  /* tslint:enable: variable-name */

  isNumber: boolean = false;
  @ViewChild("rccinput") rccInput: ElementRef;
  // tslint:disable-next-line: no-input-rename
  @Input("aria-describedby") userAriaDescribedBy: string;

  stateChanges = new Subject<void>();
  focused = false;
  controlType = "app-rcc-currency-input";
  id = `app-rcc-currency-input-${ RccCurrencyInput.nextId++ }`;
  errorState: boolean;
  autofilled?: boolean;

  /* tslint:disable: variable-name */
  private _placeHolder: string;
  private _required = false;
  private _disabled = false;
  private _value: number = 0;
  private _min: number = 0;
  private _max: number = Number.MAX_SAFE_INTEGER;
  private _step: number = 1;
  private _errorStateMatcher: ErrorStateMatcher;
  /* tslint:enable: variable-name */


  onChange = (_: any) => { };
  onTouched = () => { };

  constructor(
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
    // private currencyPipe: RccCurrencyPipe
    private currencyPipe: CurrencyPipe,
  )
  {

    focusMonitor.monitor(elementRef, true).subscribe(origin =>
    {
      if (this.focused && !origin)
      {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null)
    {
      this.ngControl.valueAccessor = this;
    }

  }

  setDisabledState(isDisabled: boolean): void
  {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  get empty()
  {
    return !this.value;
  }

  get shouldLabelFloat()
  {
    return this.focused || !this.empty;
  }

  @Input()
  get placeholder(): string
  {
    return this._placeHolder;
  }
  set placeholder(value: string)
  {
    this._placeHolder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean
  {
    return this._required;
  }
  set required(value: boolean)
  {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean
  {
    return this._disabled;
  }
  set disabled(value: boolean)
  {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  get value()
  {
    return this._value;
  }
  set value(val: number)
  {
    this._value = val;
    this.onChange(val);
    this.onTouched();
    this.stateChanges.next();
  }

  @Input()
  get errorStateMatcher(): ErrorStateMatcher
  {
    return this._errorStateMatcher;
  }
  set errorStateMatcher(val: ErrorStateMatcher)
  {
    this._errorStateMatcher = val;
    this.stateChanges.next();
  }

  @Input()
  get min(): number
  {
    return this._min;
  }
  set min(val: number)
  {
    this._min = val;
    this.stateChanges.next();
  }

  @Input()
  get max(): number
  {
    return this._max;
  }
  set max(val: number)
  {
    this._max = val;
    this.stateChanges.next();
  }

  @Input()
  get step(): number
  {
    return this._step;
  }
  set step(val: number)
  {
    this._step = val;
    this.stateChanges.next();
  }

  blockInvalidAmountKeys(event: KeyboardEvent): void
  {
    if (["e", "E", "+", "-"].includes(event.key))
    {
      event.preventDefault();
    }
  }

  blockInvalidAmountPaste(event: ClipboardEvent): void
  {
    const text = event.clipboardData?.getData("text") ?? "";

    if (/[eE+-]/.test(text))
    {
      event.preventDefault();
    }
  }

  autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void
  {
    if (!control.errors && nextElement)
    {
      this.focusMonitor.focusVia(nextElement, "program");
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void
  {
    if (control.value.length < 1)
    {
      this.focusMonitor.focusVia(prevElement, "program");
    }
  }

  ngOnDestroy()
  {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  setDescribedByIds(ids: string[])
  {
    this.elementRef.nativeElement.setAttribute("aria-describedby", ids.join(" "));
  }

  onContainerClick()
  {

  }
  get formattedValue(): string
  {
    return Number.isFinite(this.value) ? this.value.toFixed(2) : "0.00";
  }

  handleChange(value: string | number): void
  {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue))
    {
      this.value = numericValue;
    }
    else
    {
      this.value = 0;
    }
  }

  handleBlur(): void
  {
    this.isNumber = false;
  }

  handleFocus(): void
  {
    this.isNumber = true;

    setTimeout(() =>
    {
      this.rccInput.nativeElement.select();
    }, 25);
  }

  registerOnChange(fn)
  {
    this.onChange = fn;
  }

  registerOnTouched(fn)
  {
    this.onTouched = fn;
  }

  writeValue(value: number): void
  {
    this.value = Number.isFinite(value) ? value : 0;
  }
}
